import { NgModule } from '@angular/core';
import { HttpStatusCode } from '@angular/common/http';
import { RouterModule, Routes } from '@angular/router';
import { ERROR_PAGE_ROUTES, loadErrorsComponent } from './core/errors/error-page-routes';
import { STORE_ORDER_ROUTES } from './store/order/store-order.routes';

const baseRoutes: Routes = [
    //TODO: add empty path to rdirect to website home page

    {
        path: '',
        pathMatch: 'full', // ensures that this route is only matched when the full URL is '', not when it starts with ''
        loadChildren: () => import('./main-home-page/main-home-page.module').then((m) => m.MainHomePageModule),
    },
    {
        path: 'midrash',
        loadChildren: () => import('./midrash/midrash.module').then((m) => m.MidrashModule),
    },
    {
        path: 'users',
        loadChildren: () => import('./user/user.module').then((m) => m.UserModule),
    },
    {
        path: 'ask',
        loadChildren: () => import('./ask/ask.module').then((m) => m.AskModule),
    },
    {
        path: 'rabbi',
        loadChildren: () => import('./ravs/ravs.module').then((m) => m.RavsModule),
    },
    {
        path: 'article',
        loadChildren: () => import('./article/article.module').then((m) => m.ArticleModule),
    },
    {
        path: 'calendar',
        loadChildren: () => import('./calendar/calendar.module').then((m) => m.CalendarModule),
    },
    {
        // the memorial days list keeps its old /events/memorial address; the rest of /events is still MVC
        path: 'events/memorial',
        loadComponent: () => import('./calendar/memorial/memorial.component').then((m) => m.MemorialComponent),
    },
    {
        path: 'contact',
        loadChildren: () => import('./general-pages/contact/contact.module').then((m) => m.ContactModule),
    },
    {
        path: 'test',
        loadChildren: () => import('./exams/exams.module').then((m) => m.ExamsModule),
    },
    {
        path: 'generalpages',
        loadChildren: () => import('./general-pages/general-pages.module').then((m) => m.GeneralPagesModule),
    },
    {
        path: 'about',
        loadChildren: () => import('./general-pages/about/about.module').then((m) => m.AboutModule),
    },
    {
        path: 'search',
        loadChildren: () => import('./general-pages/search-page/search-page.module').then((m) => m.SearchPageModule),
    },
    {
        path: 'tags',
        loadChildren: () => import('./tags/tags-routing.module').then((m) => m.TagsRoutingModule),
    },
    {
        path: 'terms',
        loadComponent: () => import('./general-pages/terms/terms.component').then((m) => m.TermsComponent),
    },
    {
        path: 'dedication',
        children: [
            {
                path: 'datepicker',
                loadComponent: () => import('./shared/dedication-date-picker/dedication-date-picker.component').then((m) => m.DedicationDatePickerComponent),
            },
            {
                path: 'simulation',
                loadComponent: () => import('./shared/simulation/simulation.component').then((m) => m.SimulationComponent),
            },
        ],
    },
    {
        // the CMS area - internal users and answering rabbis only (component-level gating + server api authorization)
        path: 'nihul',
        loadComponent: () => import('./nihul/nihul-shell.component').then((m) => m.NihulShellComponent),
        children: [
            {
                path: '',
                pathMatch: 'full',
                loadComponent: () => import('./nihul/nihul-home.component').then((m) => m.NihulHomeComponent),
            },
            {
                path: 'midrash',
                loadChildren: () => import('./nihul/midrash/nihul-midrash.module').then((m) => m.NihulMidrashModule),
            },
            {
                path: 'ask',
                loadChildren: () => import('./nihul/ask/nihul-ask.module').then((m) => m.NihulAskModule),
            },
            {
                // the daily-halacha schedule (the /reg/nextHalacha.aspx successor) - STRUCTURE or ASK
                path: 'halacha',
                loadComponent: () => import('./nihul/nihul-halacha/nihul-halacha.component').then((m) => m.NihulHalachaComponent),
            },
            {
                // the user's own profile (name, main roles, password) - every cms account
                path: 'profile',
                loadComponent: () => import('./nihul/nihul-profile/nihul-profile.component').then((m) => m.NihulProfileComponent),
            },
            {
                // cms users management (the NewUser.aspx successor) - ADMIN accounts only (nihulAdmin flag + server-enforced)
                path: 'nihulusers',
                loadComponent: () => import('./nihul/nihul-users/nihul-users.component').then((m) => m.NihulUsersComponent),
            },
            {
                // site users (Reg_sub subscribers) management (the send/listask.asp successor) - ADMIN accounts only
                path: 'siteusers',
                loadComponent: () => import('./nihul/site-users/site-users.component').then((m) => m.SiteUsersComponent),
            },
            {
                // the general settings page (client-version thresholds + cache breakers) - DEVELOPER accounts
                path: 'settings',
                loadComponent: () => import('./nihul/nihul-versions/nihul-versions.component').then((m) => m.NihulVersionsComponent),
            },
            {
                // the settings page's old address (it was the versions-only page) - kept so
                // bookmarks and older menus keep working; same component, same gate
                path: 'versions',
                loadComponent: () => import('./nihul/nihul-versions/nihul-versions.component').then((m) => m.NihulVersionsComponent),
            },
            {
                // the promotions area (mailing-list csv downloads) - PROMOTIONS accounts only
                path: 'promotions',
                loadComponent: () => import('./nihul/promotions/nihul-promotions.component').then((m) => m.NihulPromotionsComponent),
            },
            {
                // the mailing-list download log (who took which list, when and why) - ADMIN accounts only
                path: 'promotionslog',
                loadComponent: () => import('./nihul/promotions/nihul-promotions-log.component').then((m) => m.NihulPromotionsLogComponent),
            },
            {
                // the Shabbat store area (שבתות עם ישיבה) - STORE accounts only: events list,
                // editor, terms, orders, settings (nihul-store.routes.ts)
                path: 'store',
                loadChildren: () => import('./nihul/nihul-store/nihul-store.routes').then((m) => m.NIHUL_STORE_ROUTES),
            },
            {
                // the shared filter-box navigates to <first-url-segment>/filter - land back on the nihul home
                path: 'filter',
                redirectTo: '',
            },
        ],
    },
    {
        path: 'hofeshlimud/manage',
        loadComponent: () => import('./hofeshLimud/manage/hofesh-limud-manage.component').then((m) => m.HofeshLimudManageComponent),
    },
    {
        path: 'hofeshlimud',
        loadComponent: () => import('./hofeshLimud/hofeshLimud.component').then((m) => m.HofeshLimudComponent),
    },
    {
        // the Shabbat store (שבתות עם ישיבה): the chooser and the event pages - two components,
        // so the sibling-route-config rule (same component on two configs is recreated) does not bite
        path: 'store',
        loadComponent: () => import('./store/store-home/store-home.component').then((m) => m.StoreHomeComponent),
    },
    // the order flow (/store/<slug>/order[/<step>], one matcher config) and the customer's order
    // page (/store/order/<token>) - BEFORE store/:slug, which would otherwise swallow them
    ...STORE_ORDER_ROUTES,
    {
        path: 'store/:slug',
        loadComponent: () => import('./store/event-page/event-page.component').then((m) => m.EventPageComponent),
    },
    {
        path: 'simulation',
        loadComponent: () => import('./shared/simulation/simulation.component').then((m) => m.SimulationComponent),
    },
    {
        path: ERROR_PAGE_ROUTES.notFound,
        loadComponent: loadErrorsComponent,
        data: { error: 404 },
    },
    {
        path: ERROR_PAGE_ROUTES.tooManyRequests,
        loadComponent: loadErrorsComponent,
        data: { error: HttpStatusCode.TooManyRequests },
    },
    {
        path: ERROR_PAGE_ROUTES.error,
        loadComponent: loadErrorsComponent,
        data: { error: 500 },
    },
    {
        path: '**',
        loadComponent: loadErrorsComponent,
        data: { error: 404 },
    },
];

@NgModule({
    imports: [
        RouterModule.forRoot(baseRoutes, {
            initialNavigation: 'enabledNonBlocking',
            enableTracing: false,
        }),
    ],
    exports: [RouterModule],
})
export class AppRoutingModule {}
